import {
  collection,
  deleteDoc,
  doc,
  FirestoreDataConverter,
  getDocs,
  limit,
  orderBy,
  query,
  setDoc,
  writeBatch,
} from 'firebase/firestore';
import {
  useCollectionData,
  useDocumentData,
} from 'react-firebase-hooks/firestore';
import { firestore } from '@/lib/firebase';

export type IncidentVerdict =
  | 'killed'
  | 'killed_lmk'
  | 'killed_manual'
  | 'killed_sigkill'
  | 'backgrounded'
  | 'display_sleep'
  | 'app_stuck'
  | 'crash'
  | 'unknown';

export type CollectionStatus =
  | 'pending'
  | 'collected'
  | 'adb_unreachable'
  | 'error'
  | 'skipped_disabled';

export interface Incident {
  id: string;
  ip: string;
  name: string;
  brand?: string | null;
  prevStatus?: string | null;
  detectedAt: Date | null;
  collectedAt?: Date | null;
  collectionStatus: CollectionStatus;
  verdict?: IncidentVerdict;
  pidAlive?: boolean;
  foreground?: string | null;
  wakefulness?: string | null;
  displayState?: string | null;
  memAvailableKb?: number | null;
  bootreason?: string | null;
  uptime?: string | null;
  model?: string | null;
  osVersion?: string | null;
  thermalMaxC?: number | null;
  dropboxHasCrash?: boolean;
  logcatUri?: string | null;
  snapshotUri?: string | null;
}

const incidentConverter: FirestoreDataConverter<Incident> = {
  toFirestore: () => {
    throw new Error('incidents are written by the agent');
  },
  fromFirestore: (snapshot) => {
    const d = snapshot.data();
    return {
      ...d,
      id: snapshot.id,
      detectedAt: d.detectedAt?.toDate?.() ?? null,
      collectedAt: d.collectedAt?.toDate?.() ?? null,
    } as Incident;
  },
};

/** Most-recent command-fail incidents (bounded so the page can't load thousands). */
export function useIncidents(max = 200) {
  return useCollectionData(
    query(
      collection(firestore, 'commandFailLog').withConverter(incidentConverter),
      orderBy('detectedAt', 'desc'),
      limit(max)
    ),
    { snapshotListenOptions: { includeMetadataChanges: false } }
  );
}

export function deleteIncident(id: string) {
  return deleteDoc(doc(firestore, 'commandFailLog', id));
}

// Deletes the whole collection, not just the page's bounded 200-doc view.
// Batches of 500 (the Firestore write-batch limit). Storage log files are
// left in place; the agent owns those.
export async function deleteAllIncidents(): Promise<number> {
  const snapshot = await getDocs(collection(firestore, 'commandFailLog'));
  const docs = snapshot.docs;
  for (let i = 0; i < docs.length; i += 500) {
    const batch = writeBatch(firestore);
    docs.slice(i, i + 500).forEach((d) => batch.delete(d.ref));
    await batch.commit();
  }
  return docs.length;
}

const configRef = doc(firestore, 'incidentConfig', 'global');

/** Global auto-collection toggle (default ON when the field is absent). */
export function useIncidentConfig() {
  return useDocumentData(configRef);
}

export function setCollectionEnabled(enabled: boolean) {
  return setDoc(configRef, { collectionEnabled: enabled }, { merge: true });
}
